import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Alert,
} from '../utils/webComponents';
import { 
  GroupAdd, 
  Close, 
  CheckCircle, 
  ContentCopy, 
  Share as ShareIcon 
} from '@mui/icons-material';
import { useTheme } from '../context/ThemeContext';
import { useTranslation } from '../hooks/useTranslation';
import referralService from '../services/referralService';

const ReferralModal = ({ visible, onClose }) => {
  const { theme } = useTheme();
  const { t } = useTranslation();
  const [referralLink, setReferralLink] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      loadReferralLink();
    }
  }, [visible]);

  const loadReferralLink = async () => {
    try {
      setLoading(true);
      const link = await referralService.generateReferralLink();
      setReferralLink(link);
    } catch (error) {
      Alert.alert(t('referral.error'), t('referral.failedToLoadReferralLink'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(referralLink);
      Alert.alert(t('referral.copied'), t('referral.referralLinkCopiedToClipboard'));
    } catch (error) {
      Alert.alert(t('referral.error'), t('referral.failedToCopyLink'));
    }
  };

  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: t('referral.joinDelphAIWithMe'),
          text: `${t('referral.joinMeOnDelphAI')} ${referralLink}`,
        });
      } else {
        // Fallback to copy if share is not available
        copyToClipboard();
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        Alert.alert(t('referral.error'), t('referral.failedToShareLink'));
      }
    }
  };

  if (!visible) return null;

  return (
    <View style={styles.overlay} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <View style={[styles.modal, { backgroundColor: theme.surface }]}>
        {/* Header */}
        <View style={styles.header}>
          <View style={[styles.iconContainer, { backgroundColor: theme.primaryLight }]}>
            <GroupAdd sx={{ fontSize: 24, color: theme.primary }} />
          </View>
          <View style={styles.headerText}>
            <Text style={[styles.title, { color: theme.text }]}>{t('referral.inviteFriends')}</Text>
            <Text style={[styles.subtitle, { color: theme.textSecondary }]}>
              {t('referral.shareReferralLinkAndEarnRewards')}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.closeButton}
            onPress={onClose}
          >
            <Close sx={{ fontSize: 24, color: theme.textMuted }} />
          </TouchableOpacity>
        </View>

        {/* Benefits */}
        <View style={styles.benefitsContainer}>
          <View style={styles.benefitItem}>
            <CheckCircle sx={{ fontSize: 20, color: theme.success }} />
            <Text style={[styles.benefitText, { color: theme.text }]}>
              {t('referral.youGet50AIFlashcards')}
            </Text>
          </View>
          <View style={styles.benefitItem}>
            <CheckCircle sx={{ fontSize: 20, color: theme.success }} />
            <Text style={[styles.benefitText, { color: theme.text }]}>
              {t('referral.yourFriendGets50AIFlashcards')}
            </Text>
          </View>
          <View style={styles.benefitItem}>
            <CheckCircle sx={{ fontSize: 20, color: theme.success }} />
            <Text style={[styles.benefitText, { color: theme.text }]}>
              {t('referral.unlimitedReferrals')}
            </Text>
          </View>
        </View>

        {/* Referral Link */}
        <View style={styles.linkContainer}>
          <Text style={[styles.linkLabel, { color: theme.text }]}>{t('referral.yourReferralLink')}</Text>
          <Text style={[styles.placeholderNote, { color: theme.textSecondary }]}>
            {t('referral.appStorePlaceholder')}
          </Text>
          <View style={[styles.linkInputContainer, { backgroundColor: theme.background, borderColor: theme.border }]}>
            <Text
              style={[styles.linkText, { color: theme.text }]}
            >
              {loading ? t('common.loading') : referralLink}
            </Text>
            <TouchableOpacity
              style={[styles.copyButton, { backgroundColor: theme.primary }]}
              onPress={copyToClipboard}
              disabled={loading}
            >
              <ContentCopy sx={{ fontSize: 20, color: 'white' }} />
            </TouchableOpacity>
          </View>
        </View>

        {/* Action Buttons */}
        <View style={styles.buttonContainer}>
          <TouchableOpacity
            style={[styles.shareButton, { backgroundColor: theme.primaryDark }]}
            onPress={shareLink}
            disabled={loading}
          >
            <ShareIcon sx={{ fontSize: 20, color: 'white' }} />
            <Text style={styles.shareButtonText}>{t('referral.shareLink')}</Text>
          </TouchableOpacity>
          
          <TouchableOpacity
            style={[styles.copyButtonFull, { backgroundColor: theme.primary }]}
            onPress={copyToClipboard}
            disabled={loading}
          >
            <ContentCopy sx={{ fontSize: 20, color: 'white' }} />
            <Text style={styles.copyButtonText}>{t('referral.copyLink')}</Text>
          </TouchableOpacity>
        </View>

        {/* Instructions */}
        <View style={[styles.instructionsContainer, { borderTopColor: theme.border }]}>
          <Text style={[styles.instructionsTitle, { color: theme.text }]}>{t('referral.howItWorks')}</Text>
          <Text style={[styles.instructionsText, { color: theme.textSecondary }]}>
            {t('referral.shareLinkWithFriends')}{'\n'}
            {t('referral.theySignUpUsingYourLink')}{'\n'}
            {t('referral.youBothGet50AIFlashcards')}
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 20,
    zIndex: 1000,
  },
  modal: {
    width: '100%',
    maxWidth: 400,
    borderRadius: 16,
    padding: 24,
    maxHeight: '80%',
    overflowY: 'auto',
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 24,
  },
  iconContainer: {
    width: 48,
    height: 48,
    borderRadius: 24,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  headerText: {
    flex: 1,
  },
  title: {
    fontSize: 20,
    fontWeight: '700',
    marginBottom: 4,
  },
  subtitle: {
    fontSize: 14,
    lineHeight: 20,
  },
  closeButton: {
    padding: 4,
  },
  benefitsContainer: {
    marginBottom: 24,
  },
  benefitItem: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  benefitText: {
    fontSize: 16,
    marginLeft: 12,
    fontWeight: '500',
  },
  linkContainer: {
    marginBottom: 24,
  },
  linkLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  linkInputContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  linkText: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    paddingVertical: 4,
    wordBreak: 'break-all',
  },
  placeholderNote: {
    fontSize: 12,
    fontStyle: 'italic',
    marginTop: 4,
    marginBottom: 8,
    textAlign: 'center',
  },
  copyButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    gap: 12,
    marginBottom: 24,
  },
  shareButton: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 12,
    gap: 8,
  },
  shareButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  copyButtonFull: {
    flex: 1,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    borderRadius: 12,
    gap: 8,
  },
  copyButtonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  instructionsContainer: {
    paddingTop: 16,
    borderTopWidth: 1,
  },
  instructionsTitle: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 8,
  },
  instructionsText: {
    fontSize: 14,
    lineHeight: 20,
    whiteSpace: 'pre-line',
  },
});

export default ReferralModal;
